import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useParams } from "react-router-dom";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getCardCopyMessage,
  getCardFieldLabels,
  getCardIssuerClient,
  toCardToken,
  type CardIssuerFeatures,
  type LegacyCardKey,
  type StyleOptions,
} from "utils/cards/cardViewer";
import { getPaymentCardTextStyle, toast } from "@outpave/ds";
import CompanyPaymentCard from "components/common/cards/CompanyPaymentCard";
import { Stack } from "components/layout/reusableLayouts";
import { useBrandingContext } from "context/BrandingContext";

// This is where mobile comes to see a card!

interface ephemeral {
  verified: boolean;
  data: LegacyCardKey;
}

const CardDetails: React.FC = () => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [issuerFeatures, setIssuerFeatures] = useState<CardIssuerFeatures>();
  const cardRef = useRef<HTMLDivElement>(null);
  const { isBrandingLoaded } = useBrandingContext();

  const params = useParams();
  let token = params.token;

  const handleClose = () => {
    window.ReactNativeWebView?.postMessage("close");
  };

  const verifyToken = async (token: string): Promise<ephemeral> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/verify-token/${token}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Token verification failed");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        verified: false,
        data: { issuerId: "", accountId: "", key: { id: "", secret: "" } },
      };
    }
  };

  const detailsAlreadyDisplayed = useRef(false);
  useEffect(() => {
    async function processCardData() {
      // The face has to be branded before it is read: the fields take concrete
      // values once and keep them, so mounting them first pins the default ink
      // for good.
      if (!isBrandingLoaded) return;
      if (detailsAlreadyDisplayed.current) return;
      detailsAlreadyDisplayed.current = true;
      if (!token) {
        setMessage(t("Error fetching card details"));
        return;
      }
      const tokenData = await verifyToken(token);
      if (!tokenData || tokenData.verified === false) {
        setMessage(t("Error fetching card details"));
        return;
      } else {
        setMessage("");
      }

      // Read here, not at render: the card is mounted and never remounts.
      const faceStyle = getPaymentCardTextStyle(cardRef.current) ?? undefined;
      const style: StyleOptions = {
        base: { ...faceStyle, fontSmoothing: "antialiased" },
      };

      try {
        const cardToken = toCardToken(tokenData.data);
        const client = getCardIssuerClient(cardToken);
        // The issuer's slots have to be on the page before it mounts into them.
        flushSync(() => setIssuerFeatures(client.features));
        await client.render({
          cardToken,
          selectors: {
            cardNumber: "#card-number",
            cvv: "#card-cvc",
            expirationDate: "#card-exp",
            copyCardNumber: "#card-number-copy",
          },
          style,
          onCopy: (field) => toast.success(getCardCopyMessage(field, t)),
          onLateError: () =>
            toast.error(t("SomethingWentWrongDescriptionWithSupport")),
          labels: getCardFieldLabels(t),
        });
      } catch (error) {
        console.error("An error occurred:", error);
        setIssuerFeatures(undefined);
        setMessage(t("Error fetching card details"));
      }
    }

    processCardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isBrandingLoaded]);

  return (
    <Stack
      gap="var(--space-2)"
      sx={{ alignItems: "center", marginBottom: "var(--space-9)" }}
    >
      <CompanyPaymentCard
        ref={cardRef}
        number={<div id="card-number" />}
        numberOverlay={
          issuerFeatures?.copyButton ? <div id="card-number-copy" /> : undefined
        }
        cvv={<div id="card-cvc" />}
        expiry={<div id="card-exp" />}
        overlay={message ? <span>{message}</span> : undefined}
      />
      <Button variant="primary-filled" onClick={handleClose}>
        {t("Close")}
      </Button>
    </Stack>
  );
};

export default CardDetails;
